import type { Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";

import type { Actor } from "@tento-lona/sheets";

import type { EventItem } from "../events/event-item.ts";
import { type EventMutationOp, eventReduce } from "./event-mutations.ts";
import type { CellSnapshot } from "@tento-lona/sheets/mutation";
import { MutationClientId, Overlay } from "@tento-lona/sheets/mutation";
import type { ConfirmResult } from "@tento-lona/sheets/mutation";
import {
  diffSnapshots,
  type ProjectionWrite,
} from "@tento-lona/sheets/mutation";
import { type EventId, RowNetworkKey } from "@tento-lona/sheets";

/**
 * The single optimistic writer for events (SDK 2.0 Move-4): the overlay is the
 * AUTHORITATIVE optimistic state. The projector *applies* its derived snapshot to
 * the cell store — the read substrate every renderer (week facade, month
 * `canonicalCells`, aggregate `EventsTable` index) still reads from. So the
 * readers are untouched; what changed at the cutover is that one writer (the
 * overlay), fed from one derivation, reconciles the store, instead of each gesture
 * writing one cell incrementally with a caller-supplied `prev` (the lane-drift /
 * "index vs store" split-brain Moves 1–4 existed to delete).
 *
 * Lifecycle, driven directly by the gestures (NOT `MutationController.perform`,
 * which couples submit into one call — the events gestures interleave the POST
 * with placeholder/late-delete/reconcile, so they own the submit). The caller
 * supplies the `clientId` so the placeholder lifecycle can key it to the
 * optimistic event id (`insertOptimistic` appends under `item.id`; `create`
 * confirms/drops that same id):
 *  - `apply(rowKey, base, op, clientId, actor)` — append the optimistic op,
 *    re-derive, and project the delta to the store. `actor` (the write's
 *    author) stamps the invalidations of every re-projection the op causes —
 *    its apply, ack, confirm and drop; a reaper expiry is the projector's own
 *    and carries none (the emit hook's default).
 *  - `confirm(rowKey, clientId, result)` — fold the server ack into base, drop the
 *    pending entry, re-derive + re-project (the reconcile; a CAS guard is
 *    unnecessary — a newer pending op simply wins by log order).
 *  - `drop(rowKey, clientId)` — roll the optimistic entry back out, re-project.
 *
 * Projection diff uses REFERENCE equality: the overlay structurally shares base's
 * item instances for every id its log doesn't touch, so `diffSnapshots(prev, next,
 * ===)` emits a write for exactly the op-touched ids (plus the reconcile's server
 * item) and nothing else. A redundant value-identical write is harmless — the
 * facade's content-hash short-circuit suppresses the re-render.
 */
export class EventOverlayProjector {
  readonly #overlays = new Map<
    string,
    Overlay<EventItem, EventMutationOp, EventId>
  >();
  /** The snapshot last written to the store per row — the `prev` each diff
   *  reconciles from (the projection's own record of what it last wrote, never
   *  the caller's notion of the prior event). */
  readonly #lastProjected = new Map<string, CellSnapshot<EventItem, EventId>>();
  /** When each pending op was appended (rowKey → clientId → clock ms) — the
   *  reaper's age source. Entries leave with their op (confirm / drop / reap). */
  readonly #appendedAt = new Map<string, Map<MutationClientId, number>>();
  /** ClientIds the reaper expired, kept so a LATE settle can tell "expired
   *  unsettled" apart from "user dropped it mid-flight" — `create` must fold its
   *  canonical for the former and take the late-delete recovery only for the
   *  latter. Consumed on settle (`confirm` / `drop` / `consumeReaped`); only a
   *  permanently-hung submit leaves a mark behind (rare, a few bytes). */
  readonly #reaped = new Map<string, Set<MutationClientId>>();
  /** Armed reap check per row (cancel fn) — cleared when the row's log empties. */
  readonly #reapTimers = new Map<string, () => void>();
  /**
   * The patch objects of the update ops this projector acknowledged (`ack`)
   * — the identity `isLandedUpdate` answers by. Weak: an entry lives only
   * as long as the upper write that owns the patch.
   */
  readonly #landedPatches = new WeakSet<EventOverlayProjector.UpdateOp["patch"]>();
  /** The author of each pending op (rowKey → clientId → actor), whose
   *  re-projections its invalidations are stamped with. Leaves with the op. */
  readonly #actors = new Map<string, Map<MutationClientId, Actor>>();
  #seq = 0;

  /**
   * @param sink  Carry one `ProjectionWrite` into the cell store at its lane —
   *   upsert `value` for `id` (clearing `prev`'s lane if it moved) or remove it.
   *   The single lane-placement authority (`EventsTable.applyOptimisticEdit` /
   *   `applyOptimisticDelete` over `writeCanonical`); the projection decides only
   *   WHAT changed.
   * @param emit  Fire the invalidations for a batch of writes (the partial-day
   *   timepoint range + any full-day viewport), replacing the gestures' explicit
   *   `emitInsert` / `emitUpdate`, stamped with the author of the op that
   *   caused them (`null`: a reaper expiry, the hook's own actor).
   * @param clock  Time + one-shot scheduling for the settle reaper — injectable
   *   so the hung-settle tests run on a fake clock (`DEFAULT_CLOCK` in the app).
   * @param settleDeadlineMs  How long a pending op may stay unsettled before the
   *   reaper expires it to a drop (`SETTLE_DEADLINE_MS` in the app).
   */
  readonly #settleDeadlineMs: number;
  readonly #clock: EventOverlayProjector.Clock;
  readonly #emit: (
      writes: readonly ProjectionWrite<EventItem, EventId>[],
      actor: Option<Actor>,
    ) => void;
  readonly #sink: (rowKey: RowNetworkKey, write: ProjectionWrite<EventItem, EventId>) => void;
  constructor(
    sink: (rowKey: RowNetworkKey, write: ProjectionWrite<EventItem, EventId>) => void,
    emit: (
      writes: readonly ProjectionWrite<EventItem, EventId>[],
      actor: Option<Actor>,
    ) => void,
    clock: EventOverlayProjector.Clock,
    settleDeadlineMs: number,
  ) {
    this.#sink = sink;
    this.#emit = emit;
    this.#clock = clock;
    this.#settleDeadlineMs = settleDeadlineMs;
  }

  /** Optimistic id for a projector op (distinct from the table's event ids). */
  nextClientId(): MutationClientId {
    return MutationClientId.of(`overlay-${++this.#seq}`);
  }

  /**
   * Re-seed a row's overlay base from the authoritative snapshot when quiescent
   * (absorbing background-sync drift), never double-applying a still-pending op.
   * On (re)seed the `lastProjected` record resets to the base — the store holds
   * exactly the base at that point.
   *
   * Reaps FIRST: a hung submit's pending op used to hold this gate closed
   * forever (`pending().length !== 0` → `setBase` skipped on every future
   * gesture), permanently starving the row of sync absorption. Expiring the
   * hung entry bounds the failure — the same call then absorbs the fresh base
   * for every event except the ones an expired, still-unanswered op writes
   * (`Overlay.setBase` keeps those, invariant (v)): the snapshot can carry
   * an upper layer's paint of that op's fields, which must not become base.
   */
  #ensure(
    rowKey: RowNetworkKey,
    base: CellSnapshot<EventItem, EventId>,
  ): Overlay<EventItem, EventMutationOp, EventId> {
    let overlay = this.#overlays.get(rowKey);
    if (!overlay) {
      overlay = new Overlay<EventItem, EventMutationOp, EventId>(
        base,
        eventReduce,
        EventOverlayProjector.FIELDS,
      );
      this.#overlays.set(rowKey, overlay);
      this.#lastProjected.set(rowKey, new Map(base));
      return overlay;
    }
    this.reapExpired(rowKey, this.#clock.now());
    if (overlay.pending().length === 0) {
      overlay.setBase(base);
      this.#lastProjected.set(rowKey, new Map(base));
    }
    return overlay;
  }

  /** Append the optimistic op (under the caller's `clientId`) and project the
   *  resulting delta to the store. Arms the row's reap check so the op cannot
   *  outlive `settleDeadlineMs` unsettled even with no follow-up gesture. */
  apply(
    rowKey: RowNetworkKey,
    base: CellSnapshot<EventItem, EventId>,
    op: EventMutationOp,
    clientId: MutationClientId,
    actor: Actor,
  ): void {
    this.#append(rowKey, this.#ensure(rowKey, base), op, clientId, actor);
  }

  #append(
    rowKey: RowNetworkKey,
    overlay: Overlay<EventItem, EventMutationOp, EventId>,
    op: EventMutationOp,
    clientId: MutationClientId,
    actor: Actor,
  ): void {
    overlay.append({ clientId, rowKey, op });
    let ages = this.#appendedAt.get(rowKey);
    if (!ages) this.#appendedAt.set(rowKey, ages = new Map());
    ages.set(clientId, this.#clock.now());
    let actors = this.#actors.get(rowKey);
    if (!actors) this.#actors.set(rowKey, actors = new Map());
    actors.set(clientId, actor);
    this.#armReapTimer(rowKey);
    this.#reproject(rowKey, overlay, "apply", actor);
  }

  /** The author `append` recorded for `clientId`, or `null` (reaped, or
   *  never appended here). */
  #actorOf(rowKey: RowNetworkKey, clientId: MutationClientId): Option<Actor> {
    return this.#actors.get(rowKey)?.get(clientId) ?? null;
  }

  /**
   * Append a field-level `update-event` op for an event whose CONFIRMED
   * state is `confirmed` — the caller's base with only answered writes in
   * it, never the item the patch is applied to (invariant (vi): an upper
   * layer's pending or refused write never becomes this overlay's base).
   * When no UNSETTLED op writes the event, `confirmed` becomes the
   * overlay's base value for it: the store snapshot can already carry an
   * upper layer's optimistic projection of this very write (`EventsClient`
   * paints before it reaches here), and folding the patch over that
   * projection would keep a refused field alive. While an op for the event
   * is unsettled — still pending, or expired by the reaper with its answer
   * outstanding — the base stays as it is (`Overlay.rebase` refuses,
   * invariant (v)), and the op's own settle decides what reaches base.
   */
  applyUpdate(
    rowKey: RowNetworkKey,
    base: CellSnapshot<EventItem, EventId>,
    op: EventOverlayProjector.UpdateOp,
    clientId: MutationClientId,
    confirmed: EventItem,
    actor: Actor,
  ): void {
    const overlay = this.#ensure(rowKey, base);
    overlay.rebase(op.id, confirmed);
    this.#append(rowKey, overlay, op, clientId, actor);
  }

  /**
   * Whether the reaper expired the update of event `id` that carries
   * `patch` (the op's own patch object, as handed to `applyUpdate`) and its
   * answer is still outstanding. The upper pending model (`EventsClient`)
   * asks this so an expired write leaves ITS view too — never repainted,
   * never in the item a later write is sent against — while the write
   * itself still waits for its answer to settle in issue order.
   */
  isExpiredUpdate(id: string, patch: EventOverlayProjector.UpdateOp["patch"]): boolean {
    for (const overlay of this.#overlays.values()) {
      const hit = overlay.expiredOps().some((op) =>
        op.kind === "update-event" && op.id === id && op.patch === patch
      );
      if (hit) return true;
    }
    return false;
  }

  /**
   * Whether the update op that carries `patch` (the op's own patch object,
   * as handed to `applyUpdate`) was acknowledged here (`ack`) — landed,
   * whatever the upper pending model has heard yet. The upper model
   * (`EventsClient`) folds such a write into the confirmed state it hands
   * the next `applyUpdate`: this overlay notifies the row of a landed write
   * BEFORE the upper model's own settle runs, and a write issued from that
   * notification must not rebase the event onto a confirmed state that
   * lacks the landed fields. A dropped (refused) op is
   * never landed, so a refused field still never reaches the base.
   */
  isLandedUpdate(patch: EventOverlayProjector.UpdateOp["patch"]): boolean {
    return this.#landedPatches.has(patch);
  }

  /**
   * Settle a landed field-level op: its OWN patch folds into base in issue
   * order (`Overlay.ack`) — never a whole item captured when it was issued.
   * A landed op behind an older one still in flight keeps applying and folds
   * once the older settles; a refusal of the older one (`drop`) takes back
   * only that op. `op` is the settled entry's op: when the reaper already
   * expired the entry it folds at its original issue position, so only the
   * fields no newer landed op wrote reach base (`FIELDS`) — a late answer
   * never restores an older move over a newer landed one, and an isolated
   * slow success still converges.
   */
  ack(rowKey: RowNetworkKey, clientId: MutationClientId, op: EventMutationOp): void {
    const overlay = this.#overlays.get(rowKey);
    if (!overlay) return;
    if (op.kind === "update-event") this.#landedPatches.add(op.patch);
    const actor = this.#actorOf(rowKey, clientId);
    overlay.ack(clientId, op);
    this.#settle(rowKey, clientId, overlay);
    this.#reproject(rowKey, overlay, "confirm", actor);
  }

  /** Fold the server ack into base, drop the pending entry, and re-project.
   *  Also lands a LATE ack whose entry the reaper already expired — the entry is
   *  gone (no-op filter) but the canonical still folds into base, so a
   *  slow-but-successful mutation converges to server truth. */
  confirm(
    rowKey: RowNetworkKey,
    clientId: MutationClientId,
    result: ConfirmResult<EventItem, EventId>,
  ): void {
    const overlay = this.#overlays.get(rowKey);
    if (!overlay) return;
    const actor = this.#actorOf(rowKey, clientId);
    overlay.confirm(clientId, result);
    this.#settle(rowKey, clientId, overlay);
    this.#reproject(rowKey, overlay, "confirm", actor);
  }

  /** Roll a pending optimistic op back out and re-project, stamped with the
   *  op's author: the author's own withdrawal (a placeholder the create
   *  replaced, a pending insert its own delete cancels). */
  drop(rowKey: RowNetworkKey, clientId: MutationClientId): void {
    this.#dropAs(rowKey, clientId, this.#actorOf(rowKey, clientId));
  }

  /** Take a refused (or unanswered) op back out and re-project. Stamped with
   *  the emit hook's own actor (`null` — the events table's), never the
   *  op's author: a subscriber that skips its own echo still repaints the
   *  take-back. */
  takeBack(rowKey: RowNetworkKey, clientId: MutationClientId): void {
    this.#dropAs(rowKey, clientId, null);
  }

  #dropAs(rowKey: RowNetworkKey, clientId: MutationClientId, actor: Option<Actor>): void {
    const overlay = this.#overlays.get(rowKey);
    if (!overlay) return;
    overlay.drop(clientId);
    this.#settle(rowKey, clientId, overlay);
    this.#reproject(rowKey, overlay, "drop", actor);
  }

  /** Whether a pending op with `clientId` is still in `rowKey`'s log — the
   *  overlay analog of the legacy `EventsTable.has(placeholder.id)` gate: a
   *  placeholder whose op was dropped mid-flight (a `removeLocal` during an
   *  in-flight `create`) is no longer pending, so `create` takes the
   *  late-delete branch instead of confirming it. Reaps first so an EXPIRED
   *  entry reads as not-pending at this decision point (the caller then
   *  distinguishes reaped from user-dropped via `consumeReaped`). */
  hasPending(rowKey: RowNetworkKey, clientId: MutationClientId): boolean {
    this.reapExpired(rowKey, this.#clock.now());
    return this.#overlays.get(rowKey)?.pending().some(
      (m) => m.clientId === clientId,
    ) ?? false;
  }

  /**
   * Expire every pending op in `rowKey` older than `settleDeadlineMs` at `now`:
   * roll it back (`Overlay.expire`, which keeps its issue position for a late
   * ack), remember it as reaped, and log LOUDLY — an unsettled
   * mutation past the deadline is a real defect (a hung request, a submit that
   * neither confirmed nor dropped), never business as usual. Returns how many
   * entries were expired. `now` is explicit so tests can force the deadline
   * without a fake clock; internal callers pass `clock.now()`.
   */
  reapExpired(rowKey: RowNetworkKey, now: number): number {
    const overlay = this.#overlays.get(rowKey);
    const ages = this.#appendedAt.get(rowKey);
    if (!overlay || !ages) return 0;
    const expired = overlay.pending().filter((m) => {
      const at = ages.get(m.clientId);
      return at != null && now - at > this.#settleDeadlineMs;
    });
    if (expired.length === 0) return 0;
    for (const m of expired) {
      const appendedAt = ages.get(m.clientId) ?? now;
      overlay.expire(m.clientId);
      ages.delete(m.clientId);
      this.#actors.get(rowKey)?.delete(m.clientId);
      let marks = this.#reaped.get(rowKey);
      if (!marks) this.#reaped.set(rowKey, marks = new Set());
      marks.add(m.clientId);
      Tracing.err("net")("event-overlay/reap: mutation expired unsettled", {
        rowKey,
        clientId: m.clientId,
        kind: m.op.kind,
        ageMs: now - appendedAt,
        deadlineMs: this.#settleDeadlineMs,
      });
    }
    if (overlay.pending().length === 0) this.#cancelReapTimer(rowKey);
    this.#reproject(rowKey, overlay, "reap", null);
    return expired.length;
  }

  /** Check-and-clear the reaped mark for `clientId` — `true` means the reaper
   *  expired it (vs a user-initiated drop). `create`'s reconcile consults this
   *  so a reaped-but-successful create folds its canonical instead of taking
   *  the late-delete recovery (which would delete a real server event). */
  consumeReaped(rowKey: RowNetworkKey, clientId: MutationClientId): boolean {
    const marks = this.#reaped.get(rowKey);
    if (!marks?.has(clientId)) return false;
    marks.delete(clientId);
    return true;
  }

  /** Settle bookkeeping shared by `confirm` / `drop`: forget the entry's age +
   *  reaped mark, and disarm the row's reap check once nothing pends. */
  #settle(
    rowKey: RowNetworkKey,
    clientId: MutationClientId,
    overlay: Overlay<EventItem, EventMutationOp, EventId>,
  ): void {
    this.#appendedAt.get(rowKey)?.delete(clientId);
    this.#actors.get(rowKey)?.delete(clientId);
    this.#reaped.get(rowKey)?.delete(clientId);
    if (overlay.pending().length === 0) this.#cancelReapTimer(rowKey);
  }

  /** Arm (once per row) a one-shot check so an unsettled op is reaped — and
   *  its ghost un-painted — without waiting for the next gesture. On fire it
   *  reaps what expired and re-arms for the oldest entry still pending. */
  #armReapTimer(rowKey: RowNetworkKey): void {
    this.#armReapTimerIn(rowKey, this.#settleDeadlineMs + 1);
  }

  #armReapTimerIn(rowKey: RowNetworkKey, delayMs: number): void {
    if (this.#reapTimers.has(rowKey)) return;
    const cancel = this.#clock.schedule(() => {
      this.#reapTimers.delete(rowKey);
      const now = this.#clock.now();
      this.reapExpired(rowKey, now);
      const pending = this.#overlays.get(rowKey)?.pending() ?? [];
      if (pending.length === 0) return;
      const ages = this.#appendedAt.get(rowKey);
      const oldest = Math.min(
        ...pending.map((m) => ages?.get(m.clientId) ?? now),
      );
      this.#armReapTimerIn(
        rowKey,
        Math.max(1, this.#settleDeadlineMs - (now - oldest) + 1),
      );
    }, delayMs);
    this.#reapTimers.set(rowKey, cancel);
  }

  #cancelReapTimer(rowKey: RowNetworkKey): void {
    const cancel = this.#reapTimers.get(rowKey);
    if (cancel) {
      cancel();
      this.#reapTimers.delete(rowKey);
    }
  }

  /** Cancel every armed reap check — teardown for owners (and tests) shutting
   *  the projector down with ops still mid-flight. State is untouched; only
   *  the scheduled checks are disarmed. */
  dispose(): void {
    for (const cancel of this.#reapTimers.values()) cancel();
    this.#reapTimers.clear();
  }

  /** Diff the new derive against what we last wrote, carry the delta to the
   *  store, emit invalidations, and record the new projected snapshot. The
   *  `event-overlay/project` breadcrumb is the signal ux tests assert on to
   *  confirm a mutation went through the overlay projector — otherwise
   *  indistinguishable in the logs from any cell write (both end in
   *  `events-facade/refresh`). */
  #reproject(
    rowKey: RowNetworkKey,
    overlay: Overlay<EventItem, EventMutationOp, EventId>,
    phase: "apply" | "confirm" | "drop" | "reap",
    actor: Option<Actor>,
  ): void {
    const prev = this.#lastProjected.get(rowKey) ?? new Map<EventId, EventItem>();
    const next = overlay.derive();
    const writes = diffSnapshots(prev, next, refEqual);
    for (const write of writes) this.#sink(rowKey, write);
    if (writes.length > 0) this.#emit(writes, actor);
    this.#lastProjected.set(rowKey, next);
    Tracing.log("event-overlay/project", {
      phase,
      rowKey,
      writes: writes.length,
      ids: writes.map((w) => w.id),
    });
  }
}

export namespace EventOverlayProjector {
  /** The field-level op `applyUpdate` appends and `ack` folds. */
  export type UpdateOp = Extract<EventMutationOp, { readonly kind: "update-event" }>;

  /** Time + one-shot scheduling for the settle reaper. Injectable so the
   *  hung-settle tests run deterministically; the app wires `DEFAULT_CLOCK`. */
  export interface Clock {
    now(): number;
    /** Run `fn` once after `delayMs`; returns a cancel. */
    schedule(fn: () => void, delayMs: number): () => void;
  }

  export const DEFAULT_CLOCK: Clock = {
    now: () => Date.now(),
    schedule: (fn, delayMs) => {
      const t = setTimeout(fn, delayMs);
      return () => clearTimeout(t);
    },
  };

  /**
   * How long a pending op may stay unsettled before the reaper expires it.
   * Generous against a slow provider relay (a Google create can take seconds)
   * while still bounding the two permanent failure modes: a ghost that renders
   * forever, and a row that never absorbs sync again because `ensure` refuses
   * `setBase` while anything pends.
   */
  export const SETTLE_DEADLINE_MS = 30_000;

  /** Patch fields whose explicit `null` is a write (a clear), not "unchanged". */
  export const NULL_CLEARS: ReadonlySet<string> = new Set([
    "category",
    "suggestedCategory",
    "useSuggestedCategory",
  ]);

  /** The fields `patch` writes, sorted: a key with a value, or an explicit
   *  `null` on a clearable field (`NULL_CLEARS`). */
  export function writtenFields(patch: UpdateOp["patch"]): string[] {
    return Object.entries(patch)
      .filter(([field, value]) =>
        value !== undefined && (value !== null || NULL_CLEARS.has(field))
      )
      .map(([field]) => field)
      .sort();
  }

  /**
   * What an event op writes, for the overlay's per-field issue order
   * (`Overlay.Fields`, invariant (i)). An insert or delete writes the whole
   * event; an update writes its patch's fields, with the placement fields
   * (`time`, `startDate`, `endDate` — a timed and an all-day move both place
   * the event) as ONE slot, so a late timed move cannot land over a newer
   * all-day one.
   */
  export const FIELDS: Overlay.Fields<EventMutationOp, EventId> = {
    slots: (op) => {
      switch (op.kind) {
        case "insert-event":
          return [{ id: op.item.id, field: Overlay.WHOLE }];
        case "delete-event":
          return [{ id: op.id, field: Overlay.WHOLE }];
        case "update-event": {
          const slots = new Set(writtenFields(op.patch).map(slotOf));
          return [...slots].map((field) => ({ id: op.id, field }));
        }
      }
    },
    restrict: (op, keep) => {
      if (op.kind !== "update-event") return null;
      const kept = new Set(keep.map((slot) => slot.field));
      const patch: UpdateOp["patch"] = {};
      for (const field of Object.keys(op.patch)) {
        if (kept.has(slotOf(field))) copyField(op.patch, patch, field);
      }
      return writtenFields(patch).length === 0 ? null : { ...op, patch };
    },
  };

  /** The slot a patch field folds under — see `FIELDS`. */
  function slotOf(field: string): string {
    return PLACEMENT.has(field) ? "placement" : field;
  }

  const PLACEMENT: ReadonlySet<string> = new Set(["time", "startDate", "endDate"]);

  function copyField<T extends object>(from: T, to: T, field: string): void {
    if (!isKeyOf(from, field)) return;
    to[field] = from[field];
  }

  function isKeyOf<T extends object>(obj: T, key: string): key is Extract<keyof T, string> {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }
}

/** Reference equality — see the class doc: the overlay shares base instances for
 *  untouched ids, so `===` writes exactly the changed ids (no missed writes; a
 *  value-identical new instance just emits a harmless redundant write). */
function refEqual(a: EventItem, b: EventItem): boolean {
  return a === b;
}
